import { useState } from "react";
import { ArrowRight, CheckCircle2, AlertCircle } from "lucide-react";
import { FadeUp } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const NewsletterSignup = () => {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | loading | done | error
  const [message, setMessage] = useState("");

  const onInvalid = (e) => {
    e.preventDefault();
    setState("error");
    setMessage(
      e.target.validity.valueMissing
        ? "Please enter your email address first."
        : "That doesn't look like a complete email — something like you@example.com."
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    if (state === "loading") return;
    setState("loading");
    setMessage("");
    try {
      const res = await fetch(`${API}/newsletter`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Something went wrong.");
      setState("done");
      setMessage(
        data.already_subscribed
          ? "You're already on the list — series news is coming your way."
          : "You're on the list. Series news and Book Three updates are coming your way."
      );
    } catch (err) {
      setState("error");
      setMessage(err.message || "Something went wrong. Please try again.");
    }
  };

  return (
    <section
      id="readers-list"
      data-testid="newsletter-section"
      className="relative py-24 md:py-28 bg-[#0a0a0a] border-y border-white/10 overflow-hidden"
    >
      <div
        className="absolute right-[-5%] top-0 w-[45%] h-full pointer-events-none"
        style={{ background: "radial-gradient(ellipse at center, rgba(16,185,129,0.08), transparent 65%)", filter: "blur(40px)" }}
        aria-hidden="true"
      />
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
        <FadeUp className="lg:col-span-6">
          <span className="font-tech text-[11px] uppercase tracking-[0.3em] text-sky-400">
            The Readers&rsquo; List
          </span>
          <h2 className="font-display font-light tracking-tighter leading-[1.02] text-[clamp(2rem,4vw,3.75rem)] mt-6">
            Be there when <span className="italic text-sky-300">the lights rise.</span>
          </h2>
          <p className="mt-6 max-w-md text-zinc-400 font-light leading-relaxed text-base md:text-lg">
            Series news and first word on Book Three: Aurora Legacy. One list — nothing
            else, ever.
          </p>
        </FadeUp>

        <FadeUp delay={0.1} className="lg:col-span-6">
          {state === "done" ? (
            <div
              className="flex items-start gap-4 border border-emerald-400/30 bg-emerald-400/[0.05] px-7 py-6 max-w-xl"
              data-testid="newsletter-success"
            >
              <CheckCircle2 size={22} className="text-emerald-400 shrink-0 mt-0.5" />
              <p className="text-zinc-200 font-light leading-relaxed">{message}</p>
            </div>
          ) : (
            <form onSubmit={submit} className="max-w-xl" data-testid="newsletter-form">
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onInvalid={onInvalid}
                  placeholder="you@example.com"
                  data-testid="newsletter-input"
                  aria-label="Email address"
                  className="flex-1 bg-[#050505] border border-white/15 text-white font-light px-5 py-4 placeholder:text-zinc-600 focus:outline-none focus:border-sky-400/70 transition-colors duration-300"
                />
                <button
                  type="submit"
                  data-testid="newsletter-submit"
                  disabled={state === "loading"}
                  className="group inline-flex items-center justify-center gap-3 bg-sky-500 text-black font-tech text-xs uppercase tracking-[0.25em] px-8 py-4 hover:bg-sky-400 hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0 transition-[background-color,transform,opacity] duration-300"
                >
                  {state === "loading" ? "Joining…" : "Notify me"}
                  <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5" />
                </button>
              </div>
              {state === "error" && (
                <p
                  className="mt-4 flex items-center gap-2 text-sm text-red-400 font-light"
                  data-testid="newsletter-error"
                >
                  <AlertCircle size={15} />
                  {message}
                </p>
              )}
              <p className="mt-4 font-tech text-[10px] uppercase tracking-[0.3em] text-zinc-600">
                Free · No spam · Unsubscribe anytime
              </p>
            </form>
          )}
        </FadeUp>
      </div>
    </section>
  );
};

export default NewsletterSignup;
